/*
 * One POV group's Preview section (POV→page fan-out, 2026-07-06).
 *
 * When the Step-2 selection spans MORE THAN ONE `Ad Page/POV` combination, the
 * wizard creates ONE GATED RUN PER GROUP (each with its own Settings) instead
 * of forcing a single page onto every ticket. This component owns the full
 * per-run preview lifecycle for its group — the same machinery LaunchWizard
 * runs inline for the single-group case, encapsulated so React hooks can be
 * instantiated once per run:
 *
 *   - live WS status stream (fast path) + WS-independent GET /runs/{id} poll
 *     (fallback path) until AWAITING_CONFIRM or a terminal status;
 *   - GET /runs/{id}/preview once the gate is reached;
 *   - per-ad selection LOCAL to the group (ad ids are run-scoped — a shared
 *     set across runs could collide);
 *   - its own confirm mutation + "Launch …" button (labels mirror the
 *     single-run footer CTA), honoring the wizard-global Published/Draft mode.
 *
 * After a successful confirm the section collapses to a success note with a
 * link to the run; the parent is notified via `onLaunched` so it can navigate
 * away once EVERY group has launched.
 */

import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Alert, Anchor, Button, Fieldset, Group, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { errorMessage } from "../../api/errors";
import { useConfirmRun, useRun, useRunPreview } from "../../api/queries";
import type { RunConfirm, RunDetail } from "../../api/client";
import { useRunStatus } from "../../ws/useRunStatus";
import { Step4Preview } from "./Step4Preview";
import { launchableAdIds, toggleOverride } from "./namingOverride";

/**
 * Statuses at which the preview poll stops (run won't advance further).
 * Keep in sync with LaunchWizard's PREVIEW_TERMINAL — duplicated here because
 * LaunchWizard imports this module (a re-export would cycle).
 */
const PREVIEW_TERMINAL = new Set(["failed", "aborted", "completed", "drafted"]);

export function GroupPreviewSection({
  groupKey,
  groupLabel,
  active,
  runId,
  createError,
  onRetryCreate,
  onBackToSettings,
  launchMode,
  onLaunched,
  summary,
}: {
  groupKey: string;
  /** Human label, e.g. `Female — 12 tickets`. */
  groupLabel: string;
  /** True while the wizard is on the Preview step (gates the WS + polls). */
  active: boolean;
  /** This group's preview-run id (null until its POST /runs resolves). */
  runId: string | null;
  /** This group's POST /runs failure, when creation failed. */
  createError: unknown | null;
  onRetryCreate: () => void;
  onBackToSettings: () => void;
  /** Wizard-global launch mode — applies to every group. */
  launchMode: "PUBLISHED" | "DRAFT";
  /** Reports a successful confirm upward (parent navigates when all done). */
  onLaunched: (groupKey: string, runId: string) => void;
  /** Where this group launches (page / campaign / ad set), under its name. */
  summary?: ReactNode;
}) {
  const [launched, setLaunched] = useState(false);
  const [launchError, setLaunchError] = useState<string | null>(null);
  const [selectedAdIds, setSelectedAdIds] = useState<Set<string>>(new Set());
  // 2026-10-06: tickets with blank naming cells the operator launches anyway.
  const [namingOverrides, setNamingOverrides] = useState<Set<string>>(new Set());

  // ---- Live WS stream + WS-independent poll (mirrors LaunchWizard) --------

  const runStatus = useRunStatus(runId, {
    enabled: runId !== null && active && !launched,
  });
  const wsStatus = runStatus.latest?.status?.toLowerCase() ?? null;
  const wsReady = runId !== null && wsStatus === "awaiting_confirm";

  const [pollStarted, setPollStarted] = useState(false);
  useEffect(() => {
    setPollStarted(false);
    if (runId === null || !active || launched) return;
    const t = setTimeout(() => setPollStarted(true), 1500);
    return () => clearTimeout(t);
  }, [runId, active, launched]);

  const runPoll = useRun(runId ?? "", {
    enabled: runId !== null && active && pollStarted && !wsReady && !launched,
    retry: false,
    refetchInterval: (query) => {
      const st = (query.state.data as RunDetail | undefined)?.status?.toLowerCase();
      if (st === "awaiting_confirm" || (st !== undefined && PREVIEW_TERMINAL.has(st))) {
        return false;
      }
      return 2500;
    },
  });

  const polledStatus = runPoll.data?.status?.toLowerCase() ?? null;
  const previewReady = wsReady || polledStatus === "awaiting_confirm";

  const effectiveRunStatus =
    runStatus.latest ??
    (runPoll.data && runId
      ? {
          run_id: runId,
          stage: runPoll.data.current_stage ?? "prep",
          status: runPoll.data.status,
        }
      : null);

  const previewQuery = useRunPreview(runId ?? "", previewReady && !launched, {
    retry: false,
  });

  const confirmRun = useConfirmRun(runId ?? "");

  // ---- Per-ad selection (run-scoped ids — local to this group) ------------

  function toggleAd(id: string, checked: boolean) {
    setSelectedAdIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  const previewTickets = previewQuery.data?.tickets ?? [];
  const readyTicketCount = previewTickets.filter((t) => t.ok).length;
  const allTicketsFailed = previewTickets.length > 0 && readyTicketCount === 0;
  // Held ads (blank naming cells, not overridden) are not selectable and are left
  // out of every "all N ads" count — the server excludes them from the launch too.
  const selectableAdIds = launchableAdIds(previewTickets, namingOverrides);
  const readyAdCount = selectableAdIds.length;
  const selectedAdCount = selectedAdIds.size;

  function toggleAllAds(checked: boolean) {
    setSelectedAdIds(checked ? new Set(selectableAdIds) : new Set());
  }

  function toggleNamingOverride(ticketId: string, checked: boolean) {
    const next = toggleOverride(previewTickets, namingOverrides, selectedAdIds, ticketId, checked);
    setNamingOverrides(next.overrides);
    setSelectedAdIds(next.selected);
  }

  const previewTerminalFailure =
    effectiveRunStatus !== null &&
    ["failed", "aborted"].includes(effectiveRunStatus.status.toLowerCase());

  // ---- Confirm (per-group Launch) ------------------------------------------

  function launch() {
    if (!runId || launched) return;
    setLaunchError(null);
    const isDraft = launchMode === "DRAFT";
    const body: RunConfirm = { mode: launchMode };
    if (selectedAdIds.size > 0) {
      body.selected_ad_ids = [...selectedAdIds];
    }
    if (namingOverrides.size > 0) {
      body.naming_override_ticket_ids = [...namingOverrides];
    }
    confirmRun.mutate(body, {
      onSuccess: () => {
        setLaunched(true);
        if (isDraft) {
          notifications.show({
            title: `Saved as draft — ${groupLabel}`,
            message: "Creatives staged in AdManage — not pushed to Meta.",
            color: "blue",
          });
        }
        onLaunched(groupKey, runId);
      },
      onError: (err) =>
        setLaunchError(
          isDraft ? `Could not save draft: ${errorMessage(err)}` : errorMessage(err),
        ),
    });
  }

  // CTA labels mirror the single-run footer (LaunchWizard) so both paths read
  // identically to the operator.
  const adWord = (n: number) => `ad${n !== 1 ? "s" : ""}`;
  const ctaLabel =
    launchMode === "DRAFT"
      ? selectedAdCount === 0
        ? "Select at least one ad to draft"
        : selectedAdCount === readyAdCount
          ? `Draft all ${readyAdCount} ${adWord(readyAdCount)}`
          : `Draft ${selectedAdCount} of ${readyAdCount} ${adWord(readyAdCount)}`
      : selectedAdCount === 0
        ? `Launch all ${readyAdCount} ${adWord(readyAdCount)}`
        : `Launch ${selectedAdCount} of ${readyAdCount} ${adWord(readyAdCount)}`;

  return (
    <Fieldset legend={groupLabel} mb="md" aria-label={`Preview — ${groupLabel}`}>
      {summary}
      {launched && runId ? (
        <Alert color="green" variant="light" role="status">
          {launchMode === "DRAFT" ? "Drafted" : "Launched"} —{" "}
          <Anchor component={Link} to={`/runs/${runId}`}>
            view run {runId}
          </Anchor>
        </Alert>
      ) : (
        <>
          {launchError && (
            <Alert color="red" variant="light" role="alert" mb="sm">
              {launchError}
            </Alert>
          )}
          <Step4Preview
            previewRunId={runId}
            runStatus={effectiveRunStatus}
            connectionStatus={runStatus.connection}
            previewData={previewQuery.data ?? null}
            isPreviewLoading={previewQuery.isLoading}
            createError={createError}
            onRetryCreate={onRetryCreate}
            onBackToSettings={onBackToSettings}
            selectedAdIds={selectedAdIds}
            onToggleAd={toggleAd}
            onToggleAllAds={toggleAllAds}
            draftMode={launchMode === "DRAFT"}
            namingOverrides={namingOverrides}
            onToggleNamingOverride={toggleNamingOverride}
          />
          <Group justify="flex-end" mt="sm">
            <Button
              onClick={launch}
              loading={confirmRun.isPending}
              disabled={
                confirmRun.isPending ||
                !runId ||
                !previewReady ||
                allTicketsFailed ||
                (previewTickets.length > 0 && readyAdCount === 0) ||
                previewTerminalFailure ||
                (launchMode === "DRAFT" && selectedAdCount === 0)
              }
            >
              {previewTerminalFailure
                ? "Preview failed"
                : !previewReady && createError === null
                  ? "Starting…"
                  : ctaLabel}
            </Button>
          </Group>
        </>
      )}
      {!launched && previewReady && (
        <Text fz="xs" c="dimmed" mt={4}>
          This group launches as its own run.
        </Text>
      )}
    </Fieldset>
  );
}
